import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { partTypeTire } from "../../../../constants/partTypes";
import { HashLink } from "../../../../navigation/HashLink";
import { useAddItemToCart } from "../../../../queries/useAddItemToCart";
import { useGetNewCart } from "../../../../queries/useGetNewCart";
import { useRemoveItemFromCart } from "../../../../queries/useRemoveItemFromCart";
import {
  StockItem,
  TirePartItem,
} from "../../../../queries/useSearchTiresByVehicle";
import { WheelPartItem } from "../../../../queries/useSearchWheelsByVehicle";
import { useDealerAccountInfo } from "../../../../stores/hooks/useDealerAccountInfo";
import { Button, PrimaryButton } from "../../../../ui/Button/Button";
import { useAllowPartPricing } from "../../../../util/useAllowPartPricing";
import { PriceSelectEntry } from "../../../PriceSelect/usePriceSelectState";
import { getProductModalHash } from "../../../ProductDetails/ProductModal";
import { useCartItems } from "./useCartItems";

type CartButtonProps = {
  className?: string;
  isStaggered?: boolean;
  primary?: boolean;
  tire: Readonly<TirePartItem>;
  tirePricing: PriceSelectEntry[];
  vehicleId: null | number;
  wheel: Readonly<WheelPartItem>;
  wheelPricing: PriceSelectEntry[];
};

export const CartButtonCombo: FunctionComponent<CartButtonProps> = ({
  className,
  isStaggered = false,
  primary,
  tire,
  tirePricing,
  vehicleId,
  wheel,
  wheelPricing,
}) => {
  const { t } = useTranslation();
  const addItemToCart = useAddItemToCart();
  const removeItemFromCart = useRemoveItemFromCart();
  const { dealerId, dealerNationalAccountName } = useDealerAccountInfo();
  const relatedCartItems = useCartItems({ isStaggered, tire, wheel });
  const initCart = useGetNewCart();

  const [orderItem] = relatedCartItems;
  const [tirePriceOption] = tirePricing;

  const allowTirePricing = useAllowPartPricing(
    tire,
    tirePriceOption?.pricingOption ?? null
  );

  const addToCart = async (
    orderNumber: string,
    part: TirePartItem | WheelPartItem,
    partPricing: PriceSelectEntry[]
  ): Promise<void> => {
    for await (const { combinedPart, pricingOption } of partPricing) {
      const partStock: StockItem[] =
        combinedPart.itemType === partTypeTire &&
        combinedPart.itemId === part.partDetails.primaryPart.itemId
          ? (part.partStock.primaryPart as StockItem[])
          : [];

      await addItemToCart.send([
        {
          dealerId: Number(dealerId),
          itemId: combinedPart.itemId,
          itemType: combinedPart.itemType,
          orderNumber,
          partStock,
          pricingOption,
          vehicleId,
        },
      ]);
    }
  };

  const addAllToCart = async (): Promise<void> => {
    const { orderNumber } = await initCart();
    await addToCart(orderNumber, tire, tirePricing);
    await addToCart(orderNumber, wheel, wheelPricing);
  };

  const removeAllFromCart = async (): Promise<void> => {
    for await (const item of relatedCartItems) {
      if (item) {
        await removeItemFromCart.send(item!);
      }
    }
  };

  if (!allowTirePricing) {
    return (
      <HashLink
        hash={getProductModalHash(wheel.partDetails.primaryPart.itemId)}
      >
        <Button
          aria-label="openDealerContactForm"
          className={className}
          element="a"
        >
          {t("cart.contactDealer")}
        </Button>
      </HashLink>
    );
  }

  const CartButtonElement =
    primary || dealerNationalAccountName === "Mazda" ? PrimaryButton : Button;

  return (
    <>
      <CartButtonElement
        onClick={(): void => {
          orderItem ? removeAllFromCart() : addAllToCart();
        }}
        aria-label={orderItem ? "removeFromQuote" : "addToQuote"}
        busy={addItemToCart.loading || removeItemFromCart.loading}
        className={clsx("tw-cart-button", className)}
        disabled={addItemToCart.loading || removeItemFromCart.loading}
        type="button"
      >
        {t(orderItem ? "cart.removeFromQuote" : "cart.addToQuote")}
      </CartButtonElement>
    </>
  );
};
